import PropTypes from 'prop-types';
import clsx from 'clsx';
import { MessageSquareQuote, Target, Scale } from 'lucide-react';
import { STAT_CHIP } from '../ui/StatCard';

/**
 * The round brief, in the trainer's own four-part vocabulary:
 * Context · Conflict · Challenge · Decision Tension.
 *
 * The Conflict block is the important one — each function states its position before
 * anyone chooses, which is what pre-loads the silo. A speaker with no `speakerKey` is
 * a non-playable actor (the reference has "Plant Head asks: Can't you fix while
 * running?"), rendered in neutral grey so the room can tell it apart from a team.
 */
const ScenarioBrief = ({ round, functions, projector = false }) => {
  const variantFor = (key) => functions.find((f) => f.key === key)?.variant || 'slate';

  return (
    <div className={clsx('space-y-5', projector && 'space-y-7')}>
      <div>
        <p className={clsx('text-neutral-600 dark:text-neutral-300', projector ? 'text-xl leading-relaxed' : 'text-sm leading-relaxed')}>
          {round.context}
        </p>
      </div>

      {round.conflict?.length > 0 && (
        <div>
          <div className="mb-2 flex items-center gap-1.5">
            <MessageSquareQuote size={14} strokeWidth={1.75} className="text-neutral-400" />
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              On the floor
            </h4>
          </div>
          <ul className="space-y-2">
            {round.conflict.map((line, i) => (
              <li
                key={`${line.speakerLabel}-${i}`}
                className={clsx(
                  'flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-xl px-3 py-2',
                  'bg-neutral-50 dark:bg-neutral-800/50',
                  projector && 'px-4 py-3'
                )}
              >
                <span
                  className={clsx(
                    'rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                    line.speakerKey
                      ? STAT_CHIP[variantFor(line.speakerKey)]
                      : 'bg-neutral-200 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'
                  )}
                >
                  {line.speakerLabel}
                </span>
                <span className={clsx('text-neutral-400 dark:text-neutral-500', projector ? 'text-base' : 'text-xs')}>
                  {line.verb}
                </span>
                <span className={clsx('text-neutral-700 dark:text-neutral-200', projector ? 'text-lg' : 'text-sm')}>
                  “{line.statement}”
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-xl border border-primary-100 bg-primary-50/60 px-4 py-3 dark:border-primary-500/20 dark:bg-primary-500/10">
        <div className="mb-1 flex items-center gap-1.5">
          <Target size={14} strokeWidth={1.75} className="text-primary-600 dark:text-primary-400" />
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-400">
            The call
          </h4>
        </div>
        <p className={clsx('text-neutral-800 dark:text-neutral-100', projector ? 'text-lg' : 'text-sm')}>
          {round.challenge}
        </p>
      </div>

      {round.decisionTensions?.length > 0 && (
        <div>
          <div className="mb-2 flex items-center gap-1.5">
            <Scale size={14} strokeWidth={1.75} className="text-neutral-400" />
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              What it costs you either way
            </h4>
          </div>
          <ul className="space-y-1.5">
            {round.decisionTensions.map((t) => (
              <li
                key={t}
                className={clsx(
                  'flex gap-2 text-neutral-600 dark:text-neutral-300',
                  projector ? 'text-base' : 'text-sm'
                )}
              >
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-neutral-300 dark:bg-neutral-600" />
                {t}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

ScenarioBrief.propTypes = {
  round: PropTypes.object.isRequired,
  functions: PropTypes.arrayOf(PropTypes.object).isRequired,
  projector: PropTypes.bool,
};

export default ScenarioBrief;
